import React, { useState, createContext, PropsWithChildren } from 'react';
import { Modal } from '@one-thd/sui-atomic-components';
import { IModalBasicProps } from '../models/common/modal';

type WithOrWithout<T, S> = T extends S
  ? Omit<T, keyof S> & Partial<Pick<T, keyof S>>
  : T;

export interface IModalContextValues {
  isOpened: boolean;
  openModal: <T>(
    ContentComponent: React.FC<T>,
    componentProps: PropsWithChildren<WithOrWithout<T, IModalBasicProps>>,
    modalProps?: IModalProps,
  ) => void;
  hideModal: () => void;
  setComponentProps: <T>(
    componentProps: PropsWithChildren<WithOrWithout<T, IModalBasicProps>>,
  ) => void;
}

// Props that can be added to the stencil modal
interface IModalProps {
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface IModalState<T> {
  isOpened: boolean;
  ContentComponent: React.FC<T & IModalBasicProps> | null;
  componentProps: PropsWithChildren<WithOrWithout<T, IModalBasicProps>>;
  modalProps: IModalProps;
}

interface IModalProviderProps {
  children: React.ReactNode;
}

const ModalContext = createContext<IModalContextValues | null>(null);

const ModalProvider = ({ children }: IModalProviderProps) => {
  const initialState = {
    ContentComponent: null,
    componentProps: {},
    modalProps: {},
    isOpened: false,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [modalState, setModalState] = useState<IModalState<any>>(initialState);

  /**
   * openModal function.
   *
   * @param {React.FC} ContentComponent - a modal content component
   * @param {any} [componentProps={}] - a modal content component (ContentComponent) props
   * @param {IModalProps} [modalProps={}] - a stencil modal props
   *
   * @example
   *     const BasicModalContent = ({title}: string) => <p>This is a basic modal's content. {title}</p>;
   *     // Update  stencil modal props
   *     const modalProps = { size: 'sm' };
   *
   *     openModal(BasicModalContent, { title: 'Modal Title' }, modalProps);
   */
  const openModal = <T,>(
    ContentComponent: React.FC<T>,
    componentProps: PropsWithChildren<WithOrWithout<T, IModalBasicProps>>,
    modalProps: IModalProps = {},
  ) => {
    setModalState({
      ContentComponent,
      componentProps,
      modalProps,
      isOpened: true,
    });
  };

  /**
   * hideModal function for hiding (closing) opened modal
   *
   */
  const hideModal = () => {
    setModalState({
      ContentComponent: null,
      componentProps: {},
      modalProps: {},
      isOpened: false,
    });
  };

  const setComponentProps = <T,>(
    componentProps: PropsWithChildren<WithOrWithout<T, IModalBasicProps>>,
  ) => {
    setModalState((prevModalState) => ({
      ...prevModalState,
      componentProps: { ...prevModalState.componentProps, ...componentProps },
    }));
  };

  return (
    <ModalContext.Provider
      value={{
        openModal,
        hideModal,
        isOpened: modalState.isOpened,
        setComponentProps,
      }}
    >
      <Modal
        open={modalState.isOpened}
        onClose={hideModal}
        {...modalState.modalProps}
      >
        {modalState.ContentComponent && (
          <modalState.ContentComponent
            hideModal={hideModal}
            {...modalState.componentProps}
          />
        )}
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};

export { ModalProvider, ModalContext };
